import api from './api';

/**
 * Service pour la gestion de toutes les annonces
 */
class ListingService {
    
    /**
     * Récupérer toutes les annonces avec filtres
     * @param {Object} params - Paramètres de recherche
     * @param {string} params.status - Statut des annonces ('all', 'active', 'pending', 'rejected')
     * @param {string} params.category - Slug de catégorie pour filtrer
     * @param {string} params.city - Ville pour filtrer
     * @param {number} params.minPrice - Prix minimum
     * @param {number} params.maxPrice - Prix maximum
     * @param {string} params.sort - Tri ('rating', 'price', 'date')
     * @param {string} params.sortOrder - Ordre de tri ('asc', 'desc')
     * @param {number} params.limit - Nombre d'éléments par page (par défaut: 20)
     * @param {number} params.offset - Décalage pour la pagination
     * @param {string} params.search - Terme de recherche
     * @returns {Promise<Object>} Réponse avec les listings
     */
    async getAllListings(params = {}) {
        const { 
            status = 'all', 
            category, 
            city, 
            minPrice, 
            maxPrice, 
            sort = 'rating',
            sortOrder = 'desc',
            limit = 20, 
            offset = 0,
            search 
        } = params;
        
        const queryParams = new URLSearchParams();
        
        if (category && category !== 'all') queryParams.append('category', category);
        if (city) queryParams.append('city', city);
        if (minPrice) queryParams.append('minPrice', minPrice.toString());
        if (maxPrice) queryParams.append('maxPrice', maxPrice.toString());
        if (sort) queryParams.append('sortBy', sort);
        if (sortOrder) queryParams.append('sortOrder', sortOrder.toUpperCase());
        if (limit) queryParams.append('limit', limit.toString());
        if (offset) queryParams.append('offset', offset.toString());
        if (search) queryParams.append('search', search);
        
        // Ajouter la pagination pour les endpoints qui l'utilisent
        const page = Math.floor(offset / limit) + 1;
        queryParams.append('page', page.toString());
        
        console.log('[ListingService] Fetching all listings:', params);
        
        try {
            let url = '/admin/listings/all';
            if (status && status !== 'all') {
                queryParams.append('status', status);
            }
            const finalUrl = `${url}?${queryParams}`;
            console.log('[ListingService] FINAL URL:', finalUrl);
            const response = await api.get(finalUrl);
            console.log('[ListingService] All listings fetched:', response.data);
            return response.data;
        } catch (error) {
            console.error('[ListingService] Error fetching all listings:', error);
            throw error;
        }
    }
    
    /**
     * Récupérer une annonce spécifique par ID
     * @param {string} id - ID de l'annonce
     * @returns {Promise<Object>} Données de l'annonce
     */
    async getListingById(id) {
        console.log('[ListingService] Fetching listing by ID:', id);
        
        try {
            const response = await api.get(`/listings/${id}`);
            console.log('[ListingService] Listing fetched:', response.data);
            return response.data;
        } catch (error) {
            console.error('[ListingService] Error fetching listing:', error);
            throw error;
        }
    }
    
    /**
     * Récupérer les annonces populaires
     * @param {Object} params - Paramètres
     * @param {number} params.limit - Nombre d'éléments
     * @param {number} params.offset - Décalage
     * @returns {Promise<Object>} Annonces populaires
     */
    async getPopularListings(params = {}) {
        const { limit = 10, offset = 0 } = params;
        
        const queryParams = new URLSearchParams({
            limit: limit.toString(),
            offset: offset.toString()
        });
        
        console.log('[ListingService] Fetching popular listings:', params);
        
        try {
            const response = await api.get(`/listings/popular?${queryParams}`);
            console.log('[ListingService] Popular listings fetched:', response.data);
            return response.data;
        } catch (error) {
            console.error('[ListingService] Error fetching popular listings:', error);
            throw error;
        }
    }
    
    /**
     * Récupérer les annonces par catégorie
     * @param {string} categorySlug - Slug de la catégorie
     * @param {Object} params - Paramètres additionnels
     * @returns {Promise<Object>} Annonces de la catégorie
     */
    async getListingsByCategory(categorySlug, params = {}) {
        const { limit = 10, offset = 0, sort = 'rating' } = params;
        
        const queryParams = new URLSearchParams({
            limit: limit.toString(),
            offset: offset.toString(),
            sort
        });
        
        console.log('[ListingService] Fetching listings by category:', categorySlug, params);
        
        try {
            const response = await api.get(`/listings/category/${categorySlug}?${queryParams}`);
            console.log('[ListingService] Category listings fetched:', response.data);
            return response.data;
        } catch (error) {
            console.error('[ListingService] Error fetching category listings:', error);
            throw error;
        }
    }
    
    /**
     * Récupérer les catégories disponibles
     * @returns {Promise<Object>} Liste des catégories
     */
    async getCategories() {
        console.log('[ListingService] Fetching categories');
        
        try {
            const response = await api.get('/categories');
            console.log('[ListingService] Categories fetched:', response.data);
            return response.data;
        } catch (error) {
            console.error('[ListingService] Error fetching categories:', error);
            throw error;
        }
    }
    
    /**
     * Mettre à jour une annonce
     * @param {string} id - ID de l'annonce
     * @param {Object} data - Données à mettre à jour
     * @returns {Promise<Object>} Annonce mise à jour
     */
    async updateListing(id, data) {
        console.log('[ListingService] Updating listing:', id, data);
        
        try {
            const response = await api.put(`/listings/${id}`, data);
            console.log('[ListingService] Listing updated:', response.data);
            return response.data;
        } catch (error) {
            console.error('[ListingService] Error updating listing:', error);
            throw error;
        }
    }
    
    /**
     * Supprimer une annonce (admin)
     * @param {string} id - ID de l'annonce
     * @returns {Promise<Object>} Confirmation de suppression
     */
    async deleteListing(id) {
        console.log('[ListingService] Deleting listing:', id);
        
        try {
            const response = await api.delete(`/admin/listings/${id}`);
            console.log('[ListingService] Listing deleted:', response.data);
            return response.data;
        } catch (error) {
            console.error('[ListingService] Error deleting listing:', error);
            throw error;
        }
    }

    /**
     * Supprimer plusieurs annonces en lot
     * @param {string[]} ids - Liste des IDs des annonces à supprimer
     * @returns {Promise<Object>} Confirmation de suppression avec le nombre d'éléments supprimés
     */
    async deleteListingsInBatch(ids) {
        console.log('[ListingService] Deleting listings in batch:', ids);
        
        try {
            const response = await api.post(`/admin/listings/batch-delete`, { ids });
            console.log('[ListingService] Batch deletion result:', response.data);
            return response.data;
        } catch (error) {
            console.error('[ListingService] Error deleting listings in batch:', error);
            throw error;
        }
    }
}

// Instance unique du service
const listingService = new ListingService();

export default listingService;
